/**
 * Restore Browser (Spec 201 — US4 / FR-019..023b)
 *
 * Synology/Acronis-style restore: pick a protected target (no GUIDs), select a
 * recovery point on a visual TIMELINE, review the selected point in a detail panel,
 * and launch a non-destructive restore into a NEW target. A detailed point list
 * (search/filter) backs the timeline. Built from @/components/ui per the blueprint.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, RefreshCw, CheckCircle2, AlertCircle, Play, Table2, Info } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardBody,
  DataTable,
  Button,
  Badge,
  Select,
  Checkbox,
  AppTooltip,
  EmptyState,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { useAuth } from '../../hooks/useAuth';
import { formatEastern, formatEasternTime } from '@/utils/dateFormatter';
import { restoreApi, AssessedPoint, BackupEntityType, UiLaunchOutcome, BackupTableInfo, BackupSetSummary } from '../../api/restore';
import { DefinitionCoveragePanel } from './DefinitionCoveragePanel';
import { useBackupActivity } from '../../store/backupActivityStore';
import { useTriggerBackup } from '../../hooks/useTriggerBackup';
import { RecoveryTimeline } from './RecoveryTimeline';
import { BackupSetTable } from './BackupSetTable';
import { RestoreConfirmModal } from './RestoreConfirmModal';
// Spec 204: query-in-place (US1), browse + granular restore (US2/US3), pre-flight (US4)
import { InplaceMountPanel } from './InplaceMountPanel';
import { RestorePreflightBadge } from './RestorePreflightBadge';
import { moverLabel, copyCheck } from './pointProvenance';

function fmtBytes(n: number | null): string {
  const num = Number(n); // mssql returns BIGINT (size_bytes) as a string — coerce before toFixed
  if (!Number.isFinite(num) || num <= 0) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = num;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${u[i]}`;
}

const Detail: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">{label}</div>
    <div className="truncate text-sm text-[var(--color-text-primary)]">{value}</div>
  </div>
);

const TERMINAL = ['succeeded', 'failed', 'cancelled', 'degraded', 'stopped'];
const fmtElapsed = (s: number) => (s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`);

/**
 * Live restore status — polls the container execution (FR-023a) so the operator
 * sees it running, elapsed time, and the final outcome instead of a static line.
 * The Spec-200 engine reports execution status (not per-table %), so progress is
 * shown as a running indicator + elapsed clock until the run is terminal.
 */
const RestoreRunStatus: React.FC<{ executionName: string; sourceName?: string }> = ({ executionName, sourceName }) => {
  const q = useQuery({
    queryKey: ['backup', 'restore-progress', executionName],
    queryFn: () => restoreApi.progress(executionName),
    refetchInterval: (query) => (TERMINAL.includes(String((query.state.data as { status?: string } | undefined)?.status ?? '').toLowerCase()) ? false : 4000),
  });
  const status = String(q.data?.status ?? 'Running');
  const s = status.toLowerCase();
  const running = !TERMINAL.includes(s);
  const ok = s === 'succeeded';
  const failed = s === 'failed' || s === 'degraded';
  const started = q.data?.startTime ? new Date(q.data.startTime as string) : null;
  const ended = q.data?.endTime ? new Date(q.data.endTime as string).getTime() : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [running]);
  const elapsed = started ? Math.max(0, Math.floor(((running ? now : ended ?? now) - started.getTime()) / 1000)) : null;

  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-3">
          {running ? (
            <RefreshCw className="h-5 w-5 animate-spin text-[var(--color-accent)]" />
          ) : ok ? (
            <CheckCircle2 className="h-5 w-5 text-[var(--color-status-success)]" />
          ) : (
            <AlertCircle className="h-5 w-5 text-[var(--color-status-danger)]" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-[var(--color-text-primary)]">
                {ok ? 'Restore completed' : failed ? 'Restore failed' : 'Restore running…'}
              </span>
              <Badge variant={ok ? 'success' : failed ? 'danger' : 'info'}>{status}</Badge>
            </div>
            <div className="truncate text-xs text-[var(--color-text-secondary)]">
              {executionName}
              {started ? ` · started ${formatEasternTime(started)}` : ''}
              {elapsed != null ? ` · ${fmtElapsed(elapsed)} ${running ? 'elapsed' : 'total'}` : ''}
            </div>
          </div>
          <AppTooltip title="Refresh now">
            <button type="button" onClick={() => q.refetch()} className="rounded border border-[var(--color-border)] p-1 text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-accent)]">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </AppTooltip>
        </div>
        {running && (
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
            <div className="h-full w-1/3 rounded-full" style={{ backgroundColor: 'var(--color-accent)', animation: 'restoreslide 1.4s ease-in-out infinite' }} />
          </div>
        )}
        {ok && (
          <p className="mt-2 text-sm text-[var(--color-status-success)]">
            Restored into a <strong>new</strong> item{' '}
            {sourceName ? (
              <>named <span className="font-medium text-[var(--color-text-primary)]">{sourceName}_restored_&lt;timestamp&gt;</span></>
            ) : (
              'lakehouse/warehouse'
            )}{' '}
            (non-destructive{sourceName ? <> — your original <span className="font-medium text-[var(--color-text-primary)]">{sourceName}</span> is untouched</> : ''}).
            It exists in the workspace now; run <strong>Infrastructure Sync</strong> to see it listed here.
          </p>
        )}
        {failed && <p className="mt-2 text-sm text-[var(--color-status-danger)]">The restore did not complete — check the run logs.</p>}
      </CardBody>
      <style>{`@keyframes restoreslide { 0% { transform: translateX(-120%); } 100% { transform: translateX(420%); } }`}</style>
    </Card>
  );
};

interface ActiveTarget { entityType: BackupEntityType; entityId: string; name: string; workspaceName: string | null }

export const RestoreBrowser: React.FC = () => {
  const { hasRole } = useAuth();
  const canLaunch = hasRole(['admin', 'operator']);
  const { trigger: triggerBackup } = useTriggerBackup();
  const [active, setActive] = useState<ActiveTarget | null>(null);
  const [selected, setSelected] = useState<AssessedPoint | null>(null);
  const [outcome, setOutcome] = useState<{ runId: string; result: UiLaunchOutcome } | null>(null);
  // Scroll the live status card into view when a restore launches/queues — otherwise it sits above
  // the fold and the confirm modal just appears to "close with nothing happening".
  const statusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!outcome || (outcome.result.status !== 'launched' && outcome.result.status !== 'queued')) return undefined;
    const t = setTimeout(() => statusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
    return () => clearTimeout(t);
  }, [outcome]);

  const points = useQuery({
    queryKey: ['backup', 'recovery-points', active?.entityType, active?.entityId],
    queryFn: () => restoreApi.recoveryPoints(active!.entityType, active!.entityId),
    enabled: !!active,
  });

  // Restore destination — optional target workspace (default = the source's workspace).
  const [targetWs, setTargetWs] = useState('');
  const targetsQ = useQuery({ queryKey: ['backup', 'restore-targets'], queryFn: restoreApi.restoreTargets });

  // Triage mode: restore renamed copies INTO an existing lakehouse/warehouse (default the source,
  // so you compare an old table version against the live one).
  const [intoExisting, setIntoExisting] = useState(false);
  const [targetItemId, setTargetItemId] = useState('');
  const targetSetsQ = useQuery({ queryKey: ['backup', 'backup-sets', null, null], queryFn: () => restoreApi.backupSets() });
  // Candidate "into existing" targets are the same entity type as the source (lakehouse↔lakehouse,
  // warehouse↔warehouse) — renamed copies land next to the live tables for compare/triage.
  const targetItems = (targetSetsQ.data ?? []).filter((s) => s.entityType === (active?.entityType ?? 'lakehouse'));
  const effTargetItemId = intoExisting ? targetItemId || active?.entityId || '' : '';

  // Projection: the FULL table picture at the selected point (FR-019). Loaded whenever a
  // point is selected so the detail panel's Tables/Size reflect the projection too.
  const [exploreOpen, setExploreOpen] = useState(false);
  const tablesQ = useQuery({
    queryKey: ['backup', 'rp-tables', selected?.runId],
    queryFn: () => restoreApi.tablesForRun(selected!.runId),
    enabled: !!selected,
  });
  const projSize = tablesQ.data ? tablesQ.data.tables.reduce((s, t) => s + (Number(t.sizeBytes) || 0), 0) : null;
  // Restore confirmation gate (whole vs selected tables).
  const [confirm, setConfirm] = useState<{ whole: boolean; tables: string[] } | null>(null);

  // Selective restore (FR-019b): a chosen subset of tables. Cleared when the point changes.
  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());
  useEffect(() => setSelectedTables(new Set()), [selected?.runId]);

  // Spec 204 US4: pre-flight gate — launch button disabled until pre-flight passes.
  const [preflightPassed, setPreflightPassed] = useState<boolean>(true);
  useEffect(() => { setPreflightPassed(true); }, [selected?.runId]);
  const toggleTable = (name: string) =>
    setSelectedTables((s) => {
      const next = new Set(s);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  const allTableNames = (tablesQ.data?.tables ?? []).map((t) => t.tableName);
  const allSelected = allTableNames.length > 0 && allTableNames.every((n) => selectedTables.has(n));
  // Selective (per-table) restore — checkboxes always show on a restorable lakehouse OR warehouse
  // point. Warehouse selected-tables always restore as renamed copies into an existing warehouse
  // (source by default); lakehouse can also restore a subset into a new lakehouse.
  const selectiveOk = canLaunch && !!selected?.restorable &&
    (selected?.entityType === 'lakehouse' || selected?.entityType === 'warehouse');

  // Default-select the most recent restorable point ONLY on first load (or if the current
  // selection is no longer present) — never clobber a user's manual pick. `points` refetches
  // (window focus, invalidation) return a new array reference; the previous version reset the
  // selection to the latest on every refetch, so going back in time snapped straight back to
  // the newest point and Explore always showed the last values. Functional updater reads the
  // CURRENT selection (no stale closure) so a still-valid pick is preserved across refetches.
  useEffect(() => {
    const list = points.data;
    if (!list || list.length === 0) return;
    setSelected((cur) => {
      if (cur && list.some((p) => p.runId === cur.runId)) return cur; // keep the user's valid pick
      return (
        [...list].filter((p) => p.restorable).sort((a, b) => new Date(b.pointTime).getTime() - new Date(a.pointTime).getTime())[0] ??
        [...list].sort((a, b) => new Date(b.pointTime).getTime() - new Date(a.pointTime).getTime())[0]
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points.data]);

  const selectEntity = (s: BackupSetSummary) => {
    setSelected(null);
    setOutcome(null);
    setIntoExisting(false);
    setTargetItemId('');
    setActive({ entityType: s.entityType, entityId: s.entityId, name: s.name ?? s.entityId, workspaceName: s.workspaceName });
  };

  // Spec 202 — set/release a version-level legal hold on the selected recovery point (Admin-only).
  // Immutability/legal-hold is shown as a read-only status badge only; setting/releasing
  // a hold lives on the retention screen, not the restore action row.
  const isHeld = (p: AssessedPoint): boolean => p.immutabilityState === 'held' || !!p.storageLegalHold;

  const addActivity = useBackupActivity((s) => s.add);
  // Spec 214 FR-024: on-demand test-restore (throwaway target, validated then auto-deleted).
  const launchTest = async (p: AssessedPoint) => {
    try {
      const result = await restoreApi.testRestore(p.entityType, p.entityId, p.runId);
      if (result.status === 'launched') {
        addActivity({ executionName: result.executionName, verb: 'Verify', subject: `${active?.name ?? p.entityId} (test restore)`, entityType: p.entityType, entityId: p.entityId });
      }
      setOutcome({ runId: p.runId, result });
    } catch (e) {
      setOutcome({ runId: p.runId, result: { status: 'refused', reason: (e as Error).message } });
    }
  };
  const launch = async (p: AssessedPoint) => {
    try {
      const result = await restoreApi.launch(p.entityType, p.entityId, p.runId, targetWs || undefined, undefined, effTargetItemId || undefined);
      if (result.status === 'launched') addActivity({ executionName: result.executionName, verb: 'Restore', subject: active?.name ?? p.entityId, entityType: p.entityType, entityId: p.entityId });
      setOutcome({ runId: p.runId, result });
    } catch (e) {
      // Spec 214 FR-009: a thrown launch error is a REFUSAL (422 unverified storage, missing
      // tables, preflight) — never rendered as "queued".
      setOutcome({ runId: p.runId, result: { status: 'refused', reason: (e as Error).message } });
    }
  };
  // Restore only the ticked subset of tables (FR-019b). Lakehouse: into a new lakehouse or (triage)
  // renamed copies into an existing one. Warehouse: ALWAYS renamed copies into an EXISTING warehouse
  // (the chosen target, or the source by default) — there is no per-table new-warehouse path.
  const launchSelective = async () => {
    if (!selected || selectedTables.size === 0) return;
    const tables = [...selectedTables];
    const itemTarget = selected.entityType === 'warehouse'
      ? (effTargetItemId || selected.entityId)
      : (effTargetItemId || undefined);
    try {
      const result = await restoreApi.launch(selected.entityType, selected.entityId, selected.runId, targetWs || undefined, tables, itemTarget || undefined);
      if (result.status === 'launched') {
        addActivity({
          executionName: result.executionName,
          verb: 'Restore',
          subject: `${active?.name ?? selected.entityId} · ${tables.length} table${tables.length === 1 ? '' : 's'}`,
          entityType: selected.entityType,
          entityId: selected.entityId,
        });
      }
      setOutcome({ runId: selected.runId, result });
    } catch (e) {
      setOutcome({ runId: selected.runId, result: { status: 'refused', reason: (e as Error).message } });
    }
  };

  return (
    <div className="space-y-6">
      {/* 1 — pick a protected target */}
      <Card>
        <CardHeader
          title="Restore Lakehouse / Warehouse"
          subtitle="Search and select a backup set below, choose a recovery point on the timeline, and restore into a new lakehouse/warehouse (non-destructive). Workspace definitions are below in Workspace-Complete DR."
        />
        <CardBody>
          {/* Inline, searchable table of backup sets (mirrors the Workspace-Complete DR list). */}
          <BackupSetTable
            value={active ? { entityType: active.entityType, entityId: active.entityId } : null}
            onChange={selectEntity}
          />
          {active && (
          <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-[var(--color-border)] pt-4">
            <div className="mr-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">Selected</div>
              <div className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                {active.name}
                <span className="ml-2 font-normal text-[var(--color-text-secondary)]">{active.workspaceName ?? 'Unknown workspace'} · {active.entityType}</span>
              </div>
            </div>
            {canLaunch && (
              <AppTooltip title="Run a fresh backup of this set now — progress shows in Activity">
                <Button variant="outline" onClick={() => triggerBackup({ entityType: active.entityType, entityId: active.entityId, name: active.name })}>
                  <Play className="h-4 w-4" /> Back up now
                </Button>
              </AppTooltip>
            )}
            {canLaunch && (active.entityType === 'lakehouse' || active.entityType === 'warehouse') && (
              <Select
                label="Restore as"
                className="min-w-56"
                value={intoExisting ? 'existing' : 'new'}
                onChange={(e) => setIntoExisting(e.target.value === 'existing')}
                options={[
                  { value: 'new', label: `New ${active.entityType}` },
                  { value: 'existing', label: 'Renamed table copies into existing' },
                ]}
              />
            )}
            {active && canLaunch && !intoExisting && (
              <Select
                label="New item's workspace"
                className="min-w-64"
                value={targetWs}
                onChange={(e) => setTargetWs(e.target.value)}
                options={[
                  { value: '', label: `Same as source${active.workspaceName ? ` (${active.workspaceName})` : ''}` },
                  ...(targetsQ.data ?? []).map((t) => ({ value: t.workspaceId, label: t.workspaceName })),
                ]}
              />
            )}
            {active && canLaunch && intoExisting && (
              <Select
                label={`Into ${active.entityType}`}
                className="min-w-64"
                value={targetItemId || active.entityId}
                onChange={(e) => setTargetItemId(e.target.value)}
                options={[
                  { value: active.entityId, label: `${active.name} (source · compare vs live)` },
                  ...targetItems
                    .filter((l) => l.entityId !== active.entityId)
                    .map((l) => ({ value: l.entityId, label: `${l.name}${l.workspaceName ? ` · ${l.workspaceName}` : ''}` })),
                ]}
              />
            )}
          </div>
          )}
          {active && (
            <div className="mt-3 flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
              {intoExisting ? (
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Restores the chosen tables as <strong className="text-[var(--color-text-primary)]">renamed copies</strong>{' '}
                  (<code className="rounded bg-[var(--color-bg-card)] px-1">&lt;table&gt;_restored_&lt;period&gt;</code>) into the selected <strong className="text-[var(--color-text-primary)]">existing</strong> {active.entityType} — so you can compare an old version against the live table side-by-side when triaging. The live tables are <strong className="text-[var(--color-text-primary)]">never modified</strong> (the copy has a different name). Tip: tick specific tables in Explore below.
                </p>
              ) : (
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Creates a <strong className="text-[var(--color-text-primary)]">brand-new, temporary</strong> {active.entityType} named{' '}
                  <code className="rounded bg-[var(--color-bg-card)] px-1">{active.name}_restored_&lt;timestamp&gt;</code>. Your existing{' '}
                  <strong className="text-[var(--color-text-primary)]">{active.name}</strong> is <strong className="text-[var(--color-text-primary)]">never modified or overwritten</strong>. The dropdown only picks which workspace the new {active.entityType} lands in.
                </p>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* live restore status (polls the execution — FR-023a). Scrolled into view on launch so the
          progress is never hidden above the fold after the confirm modal closes. */}
      <div ref={statusRef}>
        {outcome && outcome.result.status === 'launched' && (
          <RestoreRunStatus key={outcome.result.executionName} executionName={outcome.result.executionName} sourceName={active?.name} />
        )}
        {outcome && outcome.result.status === 'queued' && (
          <Card>
            <CardBody>
              <p className="text-sm text-[var(--color-status-warning)]">Restore queued: {outcome.result.reason}</p>
            </CardBody>
          </Card>
        )}
        {/* Spec 214 FR-009: refusals render as refusals — operators no longer wait for a
            "queued" restore that was actually rejected. */}
        {outcome && outcome.result.status === 'refused' && (
          <Card>
            <CardBody>
              <p className="text-sm text-[var(--color-status-danger)]">Restore refused: {outcome.result.reason}</p>
            </CardBody>
          </Card>
        )}
      </div>

      {/* 2 — recovery points: timeline + detail + list */}
      {active && (
        <Card>
          <CardHeader
            title={`Recovery points — ${active.name}`}
            subtitle={`${active.workspaceName ?? 'Unknown workspace'} · click a point on the timeline to select it`}
          />
          <CardBody>
            {points.isLoading ? (
              <LoadingState rows={4} />
            ) : points.isError ? (
              <ErrorState onRetry={() => points.refetch()} />
            ) : !points.data || points.data.length === 0 ? (
              <EmptyState title="No recovery points" message="This target has no backups yet." />
            ) : (
              <div className="space-y-5">
                {/* timeline */}
                <RecoveryTimeline points={points.data} selectedRunId={selected?.runId ?? null} onSelect={setSelected} />

                {/* selected-point detail + actions */}
                {selected && (
                  <div className="flex flex-wrap items-center justify-between gap-4 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
                    <div className="grid flex-1 grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3 lg:grid-cols-6">
                      <Detail label="Point in time" value={formatEastern(selected.pointTime)} />
                      <Detail label="Type" value={<Badge variant={selected.backupType === 'full' ? 'info' : 'default'}>{selected.backupType}</Badge>} />
                      <Detail label="Tables" value={tablesQ.data ? tablesQ.data.tables.length : selected.tableCount ?? '—'} />
                      {/* Spec 214 T064: the number jumps when Explore loads (chain total vs this
                          run's delta) — label which one is showing. */}
                      <Detail label={projSize != null ? 'Size (chain total)' : 'Size (this run)'} value={fmtBytes(projSize ?? selected.sizeBytes)} />
                      {/* Spec 212 T033: how this point was copied, and what its copy check proved. */}
                      {(() => {
                        const m = moverLabel(selected.entityType, selected.mover);
                        const c = copyCheck(selected.entityType, selected.integrityVerified, {
                          hashChecked: selected.objectsHashVerified,
                          files: selected.filesCompleted,
                        });
                        return (
                          <>
                            <Detail
                              label="Copied by"
                              value={
                                <AppTooltip title={m.tip}>
                                  <span className="text-sm text-[var(--color-text-primary)]">{m.label}</span>
                                </AppTooltip>
                              }
                            />
                            <Detail
                              label="Copy check"
                              value={
                                <AppTooltip title={c.tip}>
                                  {c.tone ? (
                                    <Badge variant={c.tone}>{c.label}</Badge>
                                  ) : (
                                    <span className="text-sm text-[var(--color-text-secondary)]">{c.label}</span>
                                  )}
                                </AppTooltip>
                              }
                            />
                          </>
                        );
                      })()}
                      <Detail
                        label="Immutability"
                        value={
                          isHeld(selected) ? (
                            <Badge variant="warning">legal hold</Badge>
                          ) : selected.immutabilityState === 'locked' ? (
                            <AppTooltip title={`WORM-locked until ${selected.immutableUntil ? formatEastern(selected.immutableUntil) : '—'}`}>
                              <Badge variant="info">locked</Badge>
                            </AppTooltip>
                          ) : selected.immutabilityState === 'expired' ? (
                            <Badge variant="default">lock expired</Badge>
                          ) : (
                            <span className="text-sm text-[var(--color-text-secondary)]">—</span>
                          )
                        }
                      />
                      <Detail
                        label="Status"
                        value={
                          selected.restorable ? (
                            <Badge variant="success">restorable</Badge>
                          ) : (
                            <AppTooltip title={selected.notRestorableReason ?? 'not restorable'}>
                              <Badge variant="danger">not restorable</Badge>
                            </AppTooltip>
                          )
                        }
                      />
                      {/* Spec 214 FR-035: the per-run BOUND account this point restores from
                          (Spec 208) — not the entity's current assignment. */}
                      <Detail
                        label="Restores from"
                        value={
                          selected.storageAccountName ? (
                            selected.storageBindingState === 'unverified' ? (
                              <AppTooltip title="Ambiguous binding (multiple destinations recorded) — resolve before restoring">
                                <Badge variant="warning">{selected.storageAccountName}</Badge>
                              </AppTooltip>
                            ) : (
                              <span className="text-sm text-[var(--color-text-primary)]">{selected.storageAccountName}</span>
                            )
                          ) : (
                            <span className="text-sm text-[var(--color-text-secondary)]">current assignment</span>
                          )
                        }
                      />
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <AppTooltip title="See the tables captured in this backup set at this point in time">
                        <Button variant="secondary" onClick={() => setExploreOpen((o) => !o)}>
                          <Table2 className="h-4 w-4" /> {exploreOpen ? 'Hide contents' : 'Explore'}
                        </Button>
                      </AppTooltip>
                      {canLaunch && selected.restorable ? (
                        <>
                          {/* Spec 214 FR-024: prove recoverability without keeping the copy —
                              the guidance promised this button; the endpoint existed unwired. */}
                          <AppTooltip title="Test-restore into a throwaway target that is validated then auto-deleted — proves this point recovers without keeping the copy">
                            <Button variant="secondary" onClick={() => void launchTest(selected)}>
                              <RotateCcw className="h-4 w-4" /> Test
                            </Button>
                          </AppTooltip>
                          <AppTooltip title={preflightPassed ? 'Restore this point into a new item' : 'Pre-flight checks failed — resolve them before restoring (Spec 214 FR-033)'}>
                            <Button
                              variant="primary"
                              disabled={!preflightPassed}
                              onClick={() => setConfirm({ whole: true, tables: (tablesQ.data?.tables ?? []).map((t) => t.tableName) })}
                            >
                              <RotateCcw className="h-4 w-4" /> Restore
                            </Button>
                          </AppTooltip>
                        </>
                      ) : !selected.restorable ? (
                        <span className="text-sm text-[var(--color-text-secondary)]">{selected.notRestorableReason ?? 'Not restorable'}</span>
                      ) : (
                        <span className="text-sm text-[var(--color-text-secondary)]">Read-only role</span>
                      )}
                    </div>
                  </div>
                )}

                {exploreOpen && selected && (
                  <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h4 className="font-medium text-[var(--color-text-primary)]">Contents at {formatEastern(selected.pointTime)}</h4>
                        <p className="text-xs text-[var(--color-text-secondary)]">
                          {tablesQ.data?.backupType === 'incremental' ? (
                            <>Tables restorable at this point — anchored on the full{tablesQ.data?.fullPointTime ? ` (${formatEastern(tablesQ.data.fullPointTime)})` : ''} with incremental changes applied.</>
                          ) : (
                            <>Tables in this full backup of {active?.name}.</>
                          )}
                          {selectiveOk ? ' Tick tables to restore only those, or use Restore above for the whole set.' : ''}
                        </p>
                        {tablesQ.data?.backupType === 'incremental' && tablesQ.data?.anchoredByFull && (() => {
                          // If no table has changedInPoint=true, all metrics come from the anchoring
                          // full — incremental backups don't update row counts independently.
                          const anyChanged = tablesQ.data.tables.some((t) => t.changedInPoint);
                          return !anyChanged ? (
                            <p className="mt-1 text-xs text-[var(--color-status-warning)]">
                              Row counts and sizes shown are from the anchoring full backup — they reflect the state at that time, not the current incremental point.
                            </p>
                          ) : null;
                        })()}
                        {tablesQ.data?.backupType === 'incremental' && !tablesQ.data?.anchoredByFull && (
                          <p className="mt-1 text-xs text-[var(--color-status-warning)]">No full backup anchors this point — the picture may be incomplete.</p>
                        )}
                      </div>
                      {selectiveOk && (tablesQ.data?.tables ?? []).length > 0 && (
                        <Button variant="primary" disabled={selectedTables.size === 0 || !preflightPassed} onClick={() => setConfirm({ whole: false, tables: [...selectedTables] })}>
                          <RotateCcw className="h-4 w-4" /> Restore{selectedTables.size > 0 ? ` ${selectedTables.size}` : ''} selected table{selectedTables.size === 1 ? '' : 's'}
                        </Button>
                      )}
                    </div>
                    {tablesQ.isLoading ? (
                      <LoadingState rows={3} />
                    ) : tablesQ.isError ? (
                      <ErrorState onRetry={() => tablesQ.refetch()} />
                    ) : (tablesQ.data?.tables ?? []).length === 0 ? (
                      <EmptyState title="No table inventory" message="No table-level metadata could be projected for this point (no anchoring full backup)." />
                    ) : (
                      <DataTable<BackupTableInfo>
                        minWidth="40rem"
                        columns={[
                          ...(selectiveOk
                            ? [{
                                key: 'sel',
                                header: (
                                  <Checkbox
                                    checked={allSelected}
                                    onChange={(e) => setSelectedTables(e.target.checked ? new Set(allTableNames) : new Set())}
                                    aria-label="Select all tables"
                                  />
                                ),
                                width: '3rem',
                                sortable: false,
                                render: (t: BackupTableInfo) => (
                                  <Checkbox
                                    checked={selectedTables.has(t.tableName)}
                                    onChange={() => toggleTable(t.tableName)}
                                    aria-label={`Select ${t.tableName}`}
                                  />
                                ),
                              }]
                            : []),
                          {
                            key: 'tableName',
                            header: 'Table',
                            sortable: true,
                            render: (t) => (
                              <span className="flex items-center gap-2">
                                {t.tableName}
                                {tablesQ.data?.backupType === 'incremental' && t.changedInPoint && <Badge variant="info">changed</Badge>}
                              </span>
                            ),
                          },
                          { key: 'rowCount', header: 'Rows', width: '10rem', align: 'right', sortValue: (t) => Number(t.rowCount ?? -1), render: (t) => (t.rowCount != null ? Number(t.rowCount).toLocaleString() : '—') },
                          { key: 'sizeBytes', header: 'Size', width: '9rem', align: 'right', sortValue: (t) => Number(t.sizeBytes ?? 0), render: (t) => fmtBytes(t.sizeBytes) },
                          { key: 'deltaVersion', header: 'Δ version', width: '8rem', align: 'right', render: (t) => t.deltaVersion ?? '—' },
                        ]}
                        rows={tablesQ.data?.tables ?? []}
                        rowKey={(t) => t.tableName}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Spec 204 US4 — Pre-flight validation (lakehouse restores with a target workspace).
          Table scope comes from the Explore panel's selection (the table list that always
          worked — projection-backed, point-aware, with row counts). */}
      {selected && selected.entityType === 'lakehouse' && canLaunch && targetWs && (
        <RestorePreflightBadge
          entityType={selected.entityType}
          entityId={selected.entityId}
          runId={selected.runId}
          targetWorkspaceId={targetWs}
          tablesToRestore={selectedTables.size ? [...selectedTables] : undefined}
          onResult={setPreflightPassed}
        />
      )}

      {/* Spec 204 US1 — Query-in-place shortcut mounts (lakehouse only) */}
      {selected && selected.entityType === 'lakehouse' && selected.restorable && (
        <InplaceMountPanel
          entityId={selected.entityId}
          runId={selected.runId}
        />
      )}

      {selected && confirm && (
        <RestoreConfirmModal
          open
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            if (confirm.whole) launch(selected);
            else launchSelective();
            setConfirm(null);
          }}
          entityType={selected.entityType}
          sourceName={active?.name ?? selected.entityId}
          pointTime={selected.pointTime}
          destinationWorkspace={
            targetWs
              ? targetsQ.data?.find((t) => t.workspaceId === targetWs)?.workspaceName ?? 'selected workspace'
              : `${active?.workspaceName ?? 'source workspace'} (same as source)`
          }
          tables={confirm.whole ? (confirm.tables.length ? confirm.tables : null) : confirm.tables}
          renamedInto={
            intoExisting || (active?.entityType === 'warehouse' && !!confirm && !confirm.whole)
              ? targetItems.find((l) => l.entityId === (effTargetItemId || active?.entityId))?.name ?? active?.name ?? ''
              : undefined
          }
        />
      )}

      {/* Spec 205 — Workspace-Complete DR: restore data + item definitions into a new workspace */}
      <Card>
        <CardHeader
          title="Workspace-Complete DR"
          subtitle="Rebuild a workspace's data and item definitions (notebooks, pipelines, models, reports) into a new workspace."
        />
        <CardBody>
          <DefinitionCoveragePanel />
        </CardBody>
      </Card>
    </div>
  );
};

export default RestoreBrowser;
